import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Dropdown } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import AppButton from '../../../components/AppButton';

/**
 * Multi-select filter used by the redesigned job board (Tailwind version of JobBoardFilter).
 * `value` is an array of selected option objects; `options` is either a flat list of
 * { name, value } or groups of { name, options: [...] } (e.g. Role → discipline categories).
 */

export const FilterTrigger = React.forwardRef(({ label, badge, isActive, disabled, className, ...props }, ref) => (
    <button
        ref={ref}
        type="button"
        disabled={disabled}
        className={TB.mapClass('inline-flex h-10 items-center gap-2 rounded-lg border border-solid bg-[#F6F5F9] px-3 text-sm font-medium text-gray-900 hover:border-gray-300 disabled:cursor-not-allowed disabled:opacity-50', {
            'border-primary/40 ring-2 ring-primary/10': isActive,
            'border-gray-200': !isActive,
            ...className,
        })}
        {...props}
    >
        <span>{label}</span>
        {TB.isDefined(badge) && badge !== null && (
            <span className="rounded-md bg-primary/10 px-1.5 py-0.5 text-xs font-semibold text-primary">{badge}</span>
        )}
        <DownOutlined className="text-[10px] text-gray-400" aria-hidden="true" />
    </button>
));

const TextAction = ({ onClick, children }) => (
    <button type="button" onClick={onClick} className="border-0 bg-transparent p-0 text-xs font-semibold text-primary hover:text-primary-dark">
        {children}
    </button>
);

const JobBoardFilterDropdown = ({ label, subLabel, options = [], value = [], onChange, disabled }) => {
    const [open, setOpen] = useState(false);

    const isGrouped = TB.some(options, 'options');
    const allOptions = useMemo(() => (
        isGrouped ? TB.flatten(TB.map(options, 'options')) : options
    ).filter(option => !option.disabled), [options, isGrouped]);

    const selected = useMemo(() => new Set(TB.map(value, 'value')), [value]);

    const toggleOption = option => onChange(TB.xorBy([...value], [option], 'value'));
    const selectGroup = group => onChange(TB.uniqBy([...value, ...group.options.filter(o => !o.disabled)], 'value'));
    const clearGroup = group => {
        const remove = new Set(TB.map(group.options, 'value'));
        onChange(value.filter(item => !remove.has(item.value)));
    };

    const renderOption = option => (
        <label
            key={`option-${option.value}`}
            className={TB.mapClass('flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-gray-800 hover:bg-gray-50', {
                'cursor-not-allowed opacity-50': option.disabled,
            })}
        >
            <Checkbox
                checked={selected.has(option.value)}
                disabled={option.disabled}
                onChange={() => toggleOption(option)}
            />
            <span className="min-w-0 flex-1">{option.name}</span>
        </label>
    );

    const panel = (
        <div className="flex max-h-[min(70vh,460px)] w-[min(340px,calc(100vw-2rem))] flex-col overflow-hidden rounded-xl border border-solid border-gray-100 bg-white shadow-lg">
            <div className="flex items-center justify-between gap-3 border-0 border-b border-solid border-gray-100 px-4 py-3">
                <span className="text-sm font-semibold text-gray-900">
                    {value.length || 'None'} selected
                </span>
                <span className="flex items-center gap-3">
                    <TextAction onClick={() => onChange([...allOptions])}>All</TextAction>
                    <TextAction onClick={() => onChange([])}>None</TextAction>
                </span>
            </div>

            <div className="flex-1 overflow-y-auto px-2">
                {subLabel && !isGrouped && (
                    <p className="m-0 px-2 pb-1 pt-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{subLabel}</p>
                )}

                {isGrouped ? options.map(group => (
                    <div key={`group-${group.name}`} className="pb-2">
                        <div className="sticky top-0 z-10 flex items-center justify-between bg-white px-2 pb-1 pt-2">
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{group.name}</span>
                            <span className="flex items-center gap-3">
                                <TextAction onClick={() => selectGroup(group)}>All</TextAction>
                                <TextAction onClick={() => clearGroup(group)}>None</TextAction>
                            </span>
                        </div>
                        {group.options.map(renderOption)}
                    </div>
                )) : options.map(renderOption)}
            </div>

            <div className="flex justify-end border-0 border-t border-solid border-gray-100 px-4 py-3">
                <AppButton size="sm" onClick={() => setOpen(false)}>Done</AppButton>
            </div>
        </div>
    );

    return (
        <Dropdown
            open={open}
            onOpenChange={setOpen}
            dropdownRender={() => panel}
            trigger={['click']}
            disabled={disabled}
            placement="bottomLeft"
        >
            <FilterTrigger label={label} badge={value.length} isActive={open} disabled={disabled} />
        </Dropdown>
    );
};

JobBoardFilterDropdown.propTypes = {
    label: PropTypes.node,
    subLabel: PropTypes.node,
    options: PropTypes.array,
    value: PropTypes.array,
    onChange: PropTypes.func.isRequired,
    disabled: PropTypes.bool,
};

export default JobBoardFilterDropdown;
